// App.js - Baba AI with Intelligent Tool Calling + Google Drive OAuth
// Updated: November 18, 2025

import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import './App.css';
import Sidebar from './components/Sidebar';
import FileUpload from './components/FileUpload';
import SettingsPanel from './components/SettingsPanel';
import CodeBlock from './components/CodeBlock';
import aiService from './services/aiService';
import googleDriveService from './services/googleDriveService';
import * as exportService from './services/exportService';
import * as fileService from './services/fileService';

// OAuth Callback Component
function OAuthCallback() {
  const [status, setStatus] = useState('Processing...');
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const errorParam = urlParams.get('error');

      if (errorParam) {
        setError(`Authentication failed: ${errorParam}`);
        return;
      }

      if (!code) {
        setError('No authorization code received');
        return;
      }

      try {
        setStatus('Exchanging code for tokens...');
        await googleDriveService.handleCallback(code);
        
        setStatus('Initializing Baba_Workspace...');
        await googleDriveService.initializeBabaWorkspace();
        
        setStatus('Success! Redirecting...');
        
        setTimeout(() => {
          navigate('/');
        }, 1500);
        
      } catch (err) {
        console.error('OAuth callback error:', err);
        setError(err.message || 'Authentication failed');
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      backgroundColor: '#1a1a1a',
      color: '#ffffff',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {error ? (
        <>
          <div style={{ color: '#ff6b6b', fontSize: '1.2rem', marginBottom: '1rem' }}>
            ❌ {error}
          </div>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#333',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Return to App
          </button>
        </>
      ) : (
        <>
          <div style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
            🔐 Google Drive Authentication
          </div>
          <div style={{ color: '#888', marginBottom: '1rem' }}>
            {status}
          </div>
          <div style={{
            width: '200px',
            height: '4px',
            backgroundColor: '#333',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: '100%',
              height: '100%',
              backgroundColor: '#4CAF50',
              animation: 'loading 1.5s infinite ease-in-out'
            }} />
          </div>
        </>
      )}
      
      <style>{`
        @keyframes loading {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}

// Main Chat Application
function MainApp() {
  // Chat state
  const [chats, setChats] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  // File state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load chats from localStorage on mount
  useEffect(() => {
    const savedChats = localStorage.getItem('baba-ai-chats');
    if (savedChats) {
      const parsed = JSON.parse(savedChats);
      setChats(parsed);
      if (parsed.length > 0) {
        setCurrentChatId(parsed[0].id);
        setMessages(parsed[0].messages || []);
      }
    } else {
      createNewChat();
    }
  }, []);

  // Save chats to localStorage whenever they change
  useEffect(() => {
    if (chats.length > 0) {
      localStorage.setItem('baba-ai-chats', JSON.stringify(chats));
    }
  }, [chats]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const createNewChat = () => {
    const newChat = {
      id: Date.now().toString(),
      title: 'New Chat',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    setChats(prev => [newChat, ...prev]);
    setCurrentChatId(newChat.id);
    setMessages([]);
    setUploadedFiles([]);
  };

  const switchChat = (chatId) => {
    const chat = chats.find(c => c.id === chatId);
    if (chat) {
      setCurrentChatId(chatId);
      setMessages(chat.messages || []);
      setUploadedFiles([]);
    }
  };

  const deleteChat = (chatId) => {
    const newChats = chats.filter(c => c.id !== chatId);
    setChats(newChats);
    
    if (currentChatId === chatId && newChats.length > 0) {
      setCurrentChatId(newChats[0].id);
      setMessages(newChats[0].messages || []);
    } else if (newChats.length === 0) {
      createNewChat();
    }
  };

  const renameChat = (chatId, newTitle) => {
    setChats(prev => prev.map(chat => 
      chat.id === chatId 
        ? { ...chat, title: newTitle, updatedAt: new Date().toISOString() }
        : chat
    ));
  };

  const updateCurrentChat = (newMessages) => {
    setMessages(newMessages);
    setChats(prev => prev.map(chat => 
      chat.id === currentChatId
        ? {
            ...chat,
            messages: newMessages,
            title: chat.title === 'New Chat' && newMessages.length > 0
              ? newMessages[0].content.slice(0, 50) + (newMessages[0].content.length > 50 ? '...' : '')
              : chat.title,
            updatedAt: new Date().toISOString()
          }
        : chat
    ));
  };

  const sendMessage = async () => {
    if (!input.trim() && uploadedFiles.length === 0) return;
    if (isLoading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      files: uploadedFiles.length > 0 ? uploadedFiles.map(f => ({
        name: f.name,
        type: f.type,
        content: f.content
      })) : undefined,
      timestamp: new Date().toISOString()
    };

    const newMessages = [...messages, userMessage];
    updateCurrentChat(newMessages);
    setInput('');
    setUploadedFiles([]);
    setIsLoading(true);

    try {
      // Prepare messages for API
      const apiMessages = newMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Add file context if present
      if (userMessage.files && userMessage.files.length > 0) {
        const fileContext = userMessage.files.map(f => 
          `File: ${f.name}\nType: ${f.type}\nContent:\n${f.content}`
        ).join('\n\n');
        
        apiMessages[apiMessages.length - 1].content = 
          `${fileContext}\n\nUser question: ${userMessage.content}`;
      }

      // AI Service automatically handles tool calling
      const response = await aiService.sendMessage(apiMessages);

      const assistantMessage = {
        role: 'assistant',
        content: response,
        timestamp: new Date().toISOString()
      };

      updateCurrentChat([...newMessages, assistantMessage]);
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: `Error: ${error.message}`,
        timestamp: new Date().toISOString()
      };
      updateCurrentChat([...newMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    const processedFiles = await Promise.all(
      Array.from(files).map(async (file) => {
        let content;
        if (fileService.processFile) {
          const result = await fileService.processFile(file);
          content = result.content || result.text || '';
        } else {
          content = await file.text();
        }
        
        return {
          name: file.name,
          type: file.type,
          size: file.size,
          content: content
        };
      })
    );
    
    setUploadedFiles(prev => [...prev, ...processedFiles]);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleExport = async (format) => {
    const currentChat = chats.find(c => c.id === currentChatId);
    if (!currentChat) return;

    try {
      const chatData = {
        title: currentChat.title,
        messages: currentChat.messages,
        createdAt: currentChat.createdAt
      };

      switch(format) {
        case 'txt':
          await exportService.exportAsTXT(chatData);
          break;
        case 'json':
          await exportService.exportAsJSON(chatData);
          break;
        case 'md':
          await exportService.exportAsMD(chatData);
          break;
        case 'html':
          await exportService.exportAsHTML(chatData);
          break;
        case 'csv':
          await exportService.exportAsCSV(chatData);
          break;
        case 'docx':
          await exportService.exportAsDOCX(chatData);
          break;
        case 'pdf':
          await exportService.exportAsPDF(chatData);
          break;
        default:
          await exportService.downloadAs(chatData, format);
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert(`Export failed: ${error.message}`);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        createNewChat();
      }
      
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        setSidebarOpen(prev => !prev);
      }

      if ((e.ctrlKey || e.metaKey) && e.key === ',') {
        e.preventDefault();
        setSettingsOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Render message content with code highlighting
  const renderMessage = (message) => {
    const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = codeBlockRegex.exec(message.content)) !== null) {
      if (match.index > lastIndex) {
        parts.push(
          <div key={`text-${lastIndex}`} className="message-text">
            {message.content.slice(lastIndex, match.index)}
          </div>
        );
      }

      parts.push(
        <CodeBlock
          key={`code-${match.index}`}
          code={match[2]}
          language={match[1] || 'text'}
        />
      );

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < message.content.length) {
      parts.push(
        <div key={`text-${lastIndex}`} className="message-text">
          {message.content.slice(lastIndex)}
        </div>
      );
    }

    return parts.length > 0 ? parts : <div className="message-text">{message.content}</div>;
  };

  return (
    <div className="app">
      <Sidebar
        chats={chats}
        currentChatId={currentChatId}
        onChatSelect={switchChat}
        onChatDelete={deleteChat}
        onChatRename={renameChat}
        onNewChat={createNewChat}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      <div className="main-container">
        <div className="header">
          <button 
            className="header-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title="Toggle Sidebar (Ctrl+B)"
          >
            ☰
          </button>
          
          <h1>Baba AI</h1>

          <div className="header-actions">
            <button
              className="header-btn"
              onClick={() => setSettingsOpen(!settingsOpen)}
              title="Settings (Ctrl+,)"
            >
              ⚙️
            </button>

            <div className="export-dropdown">
              <button className="header-btn" title="Export Chat">
                📥
              </button>
              <div className="export-menu">
                <button onClick={() => handleExport('txt')}>TXT</button>
                <button onClick={() => handleExport('json')}>JSON</button>
                <button onClick={() => handleExport('md')}>Markdown</button>
                <button onClick={() => handleExport('html')}>HTML</button>
                <button onClick={() => handleExport('csv')}>CSV</button>
                <button onClick={() => handleExport('docx')}>Word</button>
                <button onClick={() => handleExport('pdf')}>PDF</button>
              </div>
            </div>
          </div>
        </div>

        <div className="messages-container">
          {messages.length === 0 ? (
            <div className="empty-state">
              <h2>Welcome to Baba AI</h2>
              <p>Ask anything - I can search the web and get real-time crypto prices automatically</p>
            </div>
          ) : (
            messages.map((message, index) => (
              <div 
                key={index} 
                className={`message ${message.role}`}
              >
                <div className="message-content">
                  {renderMessage(message)}
                  {message.files && (
                    <div className="message-files">
                      {message.files.map((file, i) => (
                        <div key={i} className="file-badge">
                          📎 {file.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          {isLoading && (
            <div className="message assistant">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="input-container">
          <FileUpload
            files={uploadedFiles}
            onFileUpload={handleFileUpload}
            onFileRemove={removeFile}
          />

          <div className="input-area">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Ask anything... (I can search and get prices automatically)"
              disabled={isLoading}
            />
            <button
              onClick={sendMessage}
              disabled={isLoading || (!input.trim() && uploadedFiles.length === 0)}
              className="send-button"
            >
              {isLoading ? '⏳' : '📤'}
            </button>
          </div>
        </div>
      </div>

      {settingsOpen && (
        <SettingsPanel onClose={() => setSettingsOpen(false)} />
      )}
    </div>
  );
}

// App with Router
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MainApp />} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
